import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Globe,
  Flag,
  Search,
  AlertCircle,
  Lightbulb,
  ChevronDown,
  Eye,
  EyeOff,
  ScanText,
  Languages,
  Stamp,
} from "lucide-react";
import PageHeader from "./PageHeader";
import "./admin-tools.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

const HINT_TYPES = [
  {
    value: "extraction",
    label: "Извлечение данных",
    short: "Извлечение",
    tone: "lavender",
    Icon: ScanText,
  },
  {
    value: "translation",
    label: "Перевод",
    short: "Перевод",
    tone: "butter",
    Icon: Languages,
  },
  {
    value: "civil_extraction",
    label: "Документы РФ по шаблону (СОР, СОРБ, МВД…)",
    short: "Документы РФ",
    tone: "mint",
    Icon: Stamp,
  },
];

const SCOPES = [
  { value: "global", label: "Глобальная (все страны)", icon: Globe },
  { value: "country_specific", label: "Для конкретной страны", icon: Flag },
];

const SCOPE_FILTERS = [
  { value: "", label: "Все области" },
  { value: "global", label: "Глобальные" },
  { value: "country_specific", label: "Для стран" },
];

const plural = (n, [one, few, many]) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

const countries = [
  { code: "AZE", name: "Азербайджан" },
  { code: "ALB", name: "Албания" },
  { code: "DZA", name: "Алжир" },
  { code: "AGO", name: "Ангола" },
  { code: "AND", name: "Андорра" },
  { code: "ATG", name: "Антигуа и Барбуда" },
  { code: "USA", name: "США" },
  { code: "ARG", name: "Аргентина" },
  { code: "ARM", name: "Армения" },
  { code: "AUS", name: "Австралия" },
  { code: "AUT", name: "Австрия" },
  { code: "AFG", name: "Афганистан" },

  { code: "BHS", name: "Багамы" },
  { code: "BHR", name: "Бахрейн" },
  { code: "BGD", name: "Бангладеш" },
  { code: "BRB", name: "Барбадос" },
  { code: "BLR", name: "Беларусь" },
  { code: "BLZ", name: "Белиз" },
  { code: "BEL", name: "Бельгия" },
  { code: "BEN", name: "Бенин" },
  { code: "BGR", name: "Болгария" },
  { code: "BOL", name: "Боливия" },
  { code: "BIH", name: "Босния и Герцеговина" },
  { code: "BWA", name: "Ботсвана" },
  { code: "BRA", name: "Бразилия" },
  { code: "BRN", name: "Бруней" },
  { code: "BFA", name: "Буркина-Фасо" },
  { code: "BDI", name: "Бурundi" },
  { code: "BTN", name: "Бутан" },

  { code: "VUT", name: "Вануату" },
  { code: "VAT", name: "Ватикан" },
  { code: "GBR", name: "Великобритания" },
  { code: "HUN", name: "Венгрия" },
  { code: "VEN", name: "Венесуэла" },
  { code: "VNM", name: "Вьетнам" },

  { code: "GAB", name: "Габон" },
  { code: "HTI", name: "Гаити" },
  { code: "GUY", name: "Гайана" },
  { code: "GMB", name: "Гамбия" },
  { code: "GHA", name: "Гана" },
  { code: "GTM", name: "Гватемала" },
  { code: "GIN", name: "Гвинея" },
  { code: "GNB", name: "Гвинея-Бисау" },
  { code: "DEU", name: "Германия" },
  { code: "HND", name: "Гондурас" },
  { code: "GRD", name: "Гренада" },
  { code: "GEO", name: "Грузия" },
  { code: "GRC", name: "Греция" },

  { code: "DNK", name: "Дания" },
  { code: "DJI", name: "Джибути" },
  { code: "DMA", name: "Доминика" },
  { code: "DOM", name: "Доминиканская Республика" },
  { code: "COD", name: "Демократическая Республика Конго" },

  { code: "EGY", name: "Египет" },

  { code: "ZMB", name: "Замбия" },
  { code: "ZWE", name: "Зимбабве" },

  { code: "ISR", name: "Израиль" },
  { code: "IND", name: "Индия" },
  { code: "IDN", name: "Индонезия" },
  { code: "JOR", name: "Иордания" },
  { code: "IRQ", name: "Ирак" },
  { code: "IRN", name: "Иран" },
  { code: "IRL", name: "Ирландия" },
  { code: "ISL", name: "Исландия" },
  { code: "ESP", name: "Испания" },
  { code: "ITA", name: "Италия" },

  { code: "YEM", name: "Йемен" },

  { code: "CPV", name: "Кабо-Верде" },
  { code: "KAZ", name: "Казахстан" },
  { code: "KHM", name: "Камбоджа" },
  { code: "CMR", name: "Камерун" },
  { code: "CAN", name: "Канада" },
  { code: "QAT", name: "Катар" },
  { code: "KEN", name: "Кения" },
  { code: "CYP", name: "Кипр" },
  { code: "KGZ", name: "Кыргызстан" },
  { code: "KIR", name: "Кирибати" },
  { code: "CHN", name: "Китай" },
  { code: "COL", name: "Колумбия" },
  { code: "COM", name: "Коморы" },
  { code: "COG", name: "Республика Конго" },
  { code: "CRI", name: "Коста-Рика" },
  { code: "CIV", name: "Кот-д'Ивуар" },
  { code: "CUB", name: "Куба" },
  { code: "KWT", name: "Кувейт" },
  { code: "PRK", name: "Северная Корея" },
  { code: "KOR", name: "Южная Корея" },

  { code: "LAO", name: "Лаос" },
  { code: "LVA", name: "Латвия" },
  { code: "LSO", name: "Лесото" },
  { code: "LBR", name: "Либерия" },
  { code: "LBN", name: "Ливан" },
  { code: "LBY", name: "Ливия" },
  { code: "LTU", name: "Литва" },
  { code: "LIE", name: "Лихтенштейн" },
  { code: "LUX", name: "Люксембург" },

  { code: "MUS", name: "Маврикий" },
  { code: "MRT", name: "Мавритания" },
  { code: "MDG", name: "Мадагаскар" },
  { code: "MKD", name: "Северная Македония" },
  { code: "MWI", name: "Малави" },
  { code: "MYS", name: "Малайзия" },
  { code: "MLI", name: "Мали" },
  { code: "MDV", name: "Мальдивы" },
  { code: "MLT", name: "Мальта" },
  { code: "MAR", name: "Марокко" },
  { code: "MHL", name: "Маршалловы Острова" },
  { code: "MEX", name: "Мексика" },
  { code: "FSM", name: "Микронезия" },
  { code: "MOZ", name: "Мозамбик" },
  { code: "MDA", name: "Молдова" },
  { code: "MCO", name: "Монако" },
  { code: "MNG", name: "Монголия" },
  { code: "MMR", name: "Мьянма" },

  { code: "NAM", name: "Намибия" },
  { code: "NRU", name: "Науру" },
  { code: "NPL", name: "Непал" },
  { code: "NER", name: "Нигер" },
  { code: "NGA", name: "Нигерия" },
  { code: "NLD", name: "Нидерланды" },
  { code: "NIC", name: "Никарагуа" },
  { code: "NZL", name: "Новая Зеландия" },
  { code: "NOR", name: "Норвегия" },

  { code: "ARE", name: "ОАЭ" },
  { code: "OMN", name: "Оман" },

  { code: "PAK", name: "Пакистан" },
  { code: "PLW", name: "Палау" },
  { code: "PSE", name: "Палестина" },
  { code: "PAN", name: "Панама" },
  { code: "PNG", name: "Папуа — Новая Гвинея" },
  { code: "PRY", name: "Парагвай" },
  { code: "PER", name: "Перу" },
  { code: "POL", name: "Польша" },
  { code: "PRT", name: "Португалия" },

  { code: "RUS", name: "Россия" },
  { code: "RWA", name: "Руанда" },
  { code: "ROU", name: "Румыния" },

  { code: "SLV", name: "Сальвадор" },
  { code: "WSM", name: "Самоа" },
  { code: "SMR", name: "Сан-Марино" },
  { code: "STP", name: "Сан-Томе и Принсипи" },
  { code: "SAU", name: "Саудовская Аравия" },
  { code: "SWZ", name: "Эсватини" },
  { code: "SYC", name: "Сейшелы" },
  { code: "SEN", name: "Сенегал" },
  { code: "VCT", name: "Сент-Винсент и Гренадины" },
  { code: "KNA", name: "Сент-Китс и Невис" },
  { code: "LCA", name: "Сент-Люсия" },
  { code: "SRB", name: "Сербия" },
  { code: "SGP", name: "Сингапур" },
  { code: "SYR", name: "Сирия" },
  { code: "SVK", name: "Словакия" },
  { code: "SVN", name: "Словения" },
  { code: "SLB", name: "Соломоновы Острова" },
  { code: "SOM", name: "Сомали" },
  { code: "SDN", name: "Судан" },
  { code: "SUR", name: "Суринам" },
  { code: "SLE", name: "Сьерра-Леоне" },

  { code: "TJK", name: "Таджикистан" },
  { code: "THA", name: "Таиланд" },
  { code: "TZA", name: "Танзания" },
  { code: "TGO", name: "Того" },
  { code: "TON", name: "Тонга" },
  { code: "TTO", name: "Тринидад и Тобаго" },
  { code: "TUV", name: "Тувалу" },
  { code: "TUN", name: "Тунис" },
  { code: "TKM", name: "Туркменистан" },
  { code: "TUR", name: "Турция" },

  { code: "UGA", name: "Уганда" },
  { code: "UZB", name: "Узбекистан" },
  { code: "UKR", name: "Украина" },
  { code: "URY", name: "Уругвай" },

  { code: "FJI", name: "Фиджи" },
  { code: "PHL", name: "Филиппины" },
  { code: "FIN", name: "Финляндия" },
  { code: "FRA", name: "Франция" },

  { code: "HRV", name: "Хорватия" },

  { code: "CAF", name: "Центральноафриканская Республика" },
  { code: "TCD", name: "Чад" },
  { code: "MNE", name: "Черногория" },
  { code: "CZE", name: "Чехия" },
  { code: "CHL", name: "Чили" },

  { code: "CHE", name: "Швейцария" },
  { code: "SWE", name: "Швеция" },
  { code: "LKA", name: "Шри-Ланка" },

  { code: "ECU", name: "Эквадор" },
  { code: "GNQ", name: "Экваториальная Гвинея" },
  { code: "ERI", name: "Эритрея" },
  { code: "EST", name: "Эстония" },
  { code: "ETH", name: "Эфиопия" },

  { code: "ZAF", name: "ЮАР" },
  { code: "SSD", name: "Южный Судан" },

  { code: "JAM", name: "Ямайка" },
  { code: "JPN", name: "Япония" },
].sort((a, b) => a.name.localeCompare(b.name, "ru"));

const AIPromptHintsPage = () => {
  const [hints, setHints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterScope, setFilterScope] = useState("");
  const [editingHint, setEditingHint] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState({
    extraction: true,
    translation: true,
  });

  const [formData, setFormData] = useState({
    scope: "global",
    country_code: "",
    hint_type: "extraction",
    hint_text: "",
    priority: 0,
    is_active: true,
    description: "",
  });

  const fetchHints = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/ai-hints`);
      setHints(response.data);
      setError("");
    } catch (err) {
      setError("Не удалось загрузить подсказки");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHints();
  }, [fetchHints]);

  const handleCreateOrUpdate = async () => {
    try {
      if (!formData.hint_text.trim()) {
        alert("Введите текст подсказки");
        return;
      }

      if (editingHint) {
        await axios.put(
          `${API_BASE_URL}/api/admin/ai-hints/${editingHint.id}`,
          formData,
        );
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/ai-hints`, formData);
      }

      fetchHints();
      resetForm();
    } catch (err) {
      alert(err.response?.data?.error || "Ошибка при сохранении");
    }
  };

  const handleEdit = (hint) => {
    setEditingHint(hint);
    setIsCreating(true);
    setFormData({
      scope: hint.scope,
      country_code: hint.country_code || "",
      hint_type: hint.hint_type,
      hint_text: hint.hint_text,
      priority: hint.priority || 0,
      is_active: hint.is_active,
      description: hint.description || "",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить эту подсказку?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/ai-hints/${id}`);
      fetchHints();
    } catch (err) {
      alert("Ошибка при удалении");
    }
  };

  const handleToggleActive = async (hint) => {
    try {
      await axios.put(`${API_BASE_URL}/api/admin/ai-hints/${hint.id}`, {
        ...hint,
        is_active: !hint.is_active,
      });
      fetchHints();
    } catch (err) {
      alert("Ошибка при изменении статуса");
    }
  };

  const resetForm = () => {
    setIsCreating(false);
    setEditingHint(null);
    setFormData({
      scope: "global",
      country_code: "",
      hint_type: "extraction",
      hint_text: "",
      priority: 0,
      is_active: true,
      description: "",
    });
  };

  const toggleType = (type) => {
    setExpandedTypes((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const matchesSearchAndScope = (h) => {
    const matchesSearch =
      !searchQuery ||
      h.hint_text.toLowerCase().includes(searchQuery.toLowerCase()) ||
      h.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesScope = !filterScope || h.scope === filterScope;
    return matchesSearch && matchesScope;
  };

  const filteredHints = hints.filter((h) => {
    const matchesType = !filterType || h.hint_type === filterType;
    return matchesSearchAndScope(h) && matchesType;
  });

  // счётчики на вкладках учитывают поиск и область, но не сам тип
  const typeCounts = hints.filter(matchesSearchAndScope).reduce((acc, h) => {
    acc[h.hint_type] = (acc[h.hint_type] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    return acc;
  }, {});

  const groupedHints = filteredHints.reduce((acc, hint) => {
    if (!acc[hint.hint_type]) acc[hint.hint_type] = [];
    acc[hint.hint_type].push(hint);
    return acc;
  }, {});

  Object.keys(groupedHints).forEach((type) => {
    groupedHints[type].sort((a, b) => (b.priority || 0) - (a.priority || 0));
  });

  const header = (
    <PageHeader
      title="Подсказки для системы"
      subtitle="Дополнительные инструкции для улучшения качества распознавания"
    >
      <button
        type="button"
        className="px-btn px-btn-ink"
        onClick={() => setIsCreating(true)}
      >
        <Plus size={20} strokeWidth={2.5} aria-hidden="true" />
        Добавить подсказку
      </button>
    </PageHeader>
  );

  if (loading) {
    return (
      <div className="tool-page">
        {header}
        <div className="px-empty" role="status">
          Загрузка...
        </div>
      </div>
    );
  }

  const setField = (key) => (e) =>
    setFormData({ ...formData, [key]: e.target.value });

  return (
    <div className="tool-page">
      {header}

      {error && (
        <div className="tool-alert" role="alert">
          <AlertCircle size={20} aria-hidden="true" />
          {error}
        </div>
      )}

      {isCreating && (
        <section
          className="res-card tool-form"
          aria-labelledby="hint-form-title"
        >
          <div className="res-card-head tool-card-head">
            <div>
              <h2 className="res-card-title" id="hint-form-title">
                {editingHint ? "Редактирование подсказки" : "Новая подсказка"}
              </h2>
            </div>
            <button
              type="button"
              className="px-icon-btn is-small"
              onClick={resetForm}
              aria-label="Отмена"
              title="Отмена"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          <div className="rec-fields">
            <label className="rec-field">
              <span className="rec-label">
                Тип подсказки
                <span className="tool-req" aria-hidden="true">
                  *
                </span>
              </span>
              <select
                className="rec-select"
                value={formData.hint_type}
                onChange={setField("hint_type")}
                aria-required="true"
              >
                {HINT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="rec-field">
              <span className="rec-label">
                Область действия
                <span className="tool-req" aria-hidden="true">
                  *
                </span>
              </span>
              <select
                className="rec-select"
                value={formData.scope}
                onChange={setField("scope")}
                aria-required="true"
              >
                {SCOPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>

            {formData.scope === "country_specific" && (
              <label className="rec-field">
                <span className="rec-label">
                  Код страны
                  <span className="tool-req" aria-hidden="true">
                    *
                  </span>
                </span>
                <select
                  className={`rec-select${formData.country_code ? "" : " is-empty"}`}
                  value={formData.country_code}
                  onChange={setField("country_code")}
                  aria-required="true"
                >
                  <option value="">Выберите...</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="rec-field">
              <label className="rec-label" htmlFor="hint-priority">
                Приоритет
              </label>
              <input
                id="hint-priority"
                className="tool-input"
                type="number"
                value={formData.priority}
                aria-describedby="hint-priority-help"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    priority: parseInt(e.target.value) || 0,
                  })
                }
              />
              <span className="tool-help" id="hint-priority-help">
                Чем выше, тем раньше применяется
              </span>
            </div>

            <label className="rec-field tool-field-wide">
              <span className="rec-label">Описание (для админа)</span>
              <input
                className="tool-input"
                type="text"
                placeholder="Краткое описание назначения подсказки"
                value={formData.description}
                onChange={setField("description")}
              />
            </label>

            <label className="rec-field tool-field-wide">
              <span className="rec-label">
                Текст подсказки
                <span className="tool-req" aria-hidden="true">
                  *
                </span>
              </span>
              <textarea
                className="tool-input is-mono"
                rows={8}
                placeholder="Введите инструкцию для системы..."
                value={formData.hint_text}
                onChange={setField("hint_text")}
                aria-required="true"
              />
            </label>
          </div>

          <label className="rec-checkbox">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData({ ...formData, is_active: e.target.checked })
              }
            />
            Активна (применяется при распознавании)
          </label>

          <div className="tool-form-actions">
            <button
              type="button"
              className="px-btn px-btn-outline"
              onClick={resetForm}
            >
              Отмена
            </button>
            <button
              type="button"
              className="px-btn px-btn-ink"
              onClick={handleCreateOrUpdate}
              disabled={!formData.hint_text.trim()}
            >
              <Save size={18} aria-hidden="true" />
              {editingHint ? "Сохранить изменения" : "Создать подсказку"}
            </button>
          </div>
        </section>
      )}

      <div className="tool-filters">
        <div className="tool-filter-row">
          <div
            className="hist-tabs"
            role="radiogroup"
            aria-label="Тип подсказки"
          >
            {[{ value: "", short: "Все", tone: null }, ...HINT_TYPES].map(
              (type) => {
                const active = filterType === type.value;
                return (
                  <button
                    key={type.value || "all"}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`hist-tab${type.tone ? ` tone-${type.tone}` : ""}${active ? " is-active" : ""}`}
                    onClick={() => setFilterType(type.value)}
                  >
                    {type.short}
                    <span className="hist-tab-count">
                      {typeCounts[type.value || "all"] || 0}
                    </span>
                  </button>
                );
              },
            )}
          </div>
        </div>
        <div className="tool-filter-row">
          <div
            className="tool-seg"
            role="radiogroup"
            aria-label="Область действия"
          >
            {SCOPE_FILTERS.map((s) => (
              <button
                key={s.value || "all"}
                type="button"
                role="radio"
                aria-checked={filterScope === s.value}
                onClick={() => setFilterScope(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <label className="hist-search">
            <Search size={18} aria-hidden="true" />
            <input
              type="search"
              aria-label="Поиск по тексту или описанию"
              placeholder="Поиск по тексту или описанию"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </label>
        </div>
      </div>

      {Object.keys(groupedHints).length === 0 ? (
        <div className="px-empty tool-empty">
          <span className="tool-empty-icon tone-butter" aria-hidden="true">
            <Lightbulb size={26} />
          </span>
          Нет подсказок, соответствующих фильтрам
        </div>
      ) : (
        Object.entries(groupedHints).map(([type, typeHints]) => {
          const typeConfig = HINT_TYPES.find((t) => t.value === type);
          const isExpanded = expandedTypes[type] !== false;
          const TypeIcon = typeConfig?.Icon || Lightbulb;
          const bodyId = `hint-group-${type}`;

          return (
            <section key={type} className="res-card tool-group">
              <h2 className="tool-group-heading">
                <button
                  type="button"
                  className="tool-group-toggle"
                  onClick={() => toggleType(type)}
                  aria-expanded={isExpanded}
                  aria-controls={bodyId}
                >
                  <span
                    className={`tool-group-icon${typeConfig ? ` tone-${typeConfig.tone}` : ""}`}
                    aria-hidden="true"
                  >
                    <TypeIcon size={22} />
                  </span>
                  <span className="tool-group-text">
                    <span className="res-card-title">
                      {typeConfig?.label || type}
                    </span>
                    <span className="tool-group-count">
                      {typeHints.length}{" "}
                      {plural(typeHints.length, [
                        "подсказка",
                        "подсказки",
                        "подсказок",
                      ])}
                    </span>
                  </span>
                  <span className="tool-chevron" aria-hidden="true">
                    <ChevronDown size={20} strokeWidth={2.5} />
                  </span>
                </button>
              </h2>

              {isExpanded && (
                <div className="tool-group-body" id={bodyId}>
                  {typeHints.map((hint) => {
                    const scopeLabel =
                      hint.scope === "global"
                        ? "Глобальная"
                        : `${countries.find((c) => c.code === hint.country_code)?.name || hint.country_code}`;
                    return (
                      <article
                        key={hint.id}
                        className={`tool-hint${hint.is_active ? "" : " is-off"}`}
                      >
                        <div className="tool-hint-head">
                          <div className="tool-hint-info">
                            <div className="tool-hint-meta">
                              <span
                                className={`tool-scope ${hint.scope === "global" ? "is-global" : "is-country"}`}
                              >
                                {hint.scope === "global" ? (
                                  <Globe size={16} aria-hidden="true" />
                                ) : (
                                  <Flag size={16} aria-hidden="true" />
                                )}
                                {scopeLabel}
                              </span>
                              <span className="res-badge is-soft">
                                Приоритет: {hint.priority}
                              </span>
                              {!hint.is_active && (
                                <span className="res-badge is-warn">
                                  Отключена
                                </span>
                              )}
                            </div>
                            {hint.description && (
                              <p className="tool-hint-desc">
                                {hint.description}
                              </p>
                            )}
                          </div>

                          <div className="tool-actions">
                            <button
                              type="button"
                              className={`px-icon-btn is-small${hint.is_active ? " is-on" : ""}`}
                              onClick={() => handleToggleActive(hint)}
                              aria-label={
                                hint.is_active
                                  ? "Деактивировать"
                                  : "Активировать"
                              }
                              title={
                                hint.is_active
                                  ? "Деактивировать"
                                  : "Активировать"
                              }
                            >
                              {hint.is_active ? (
                                <Eye size={18} aria-hidden="true" />
                              ) : (
                                <EyeOff size={18} aria-hidden="true" />
                              )}
                            </button>
                            <button
                              type="button"
                              className="px-icon-btn is-small"
                              onClick={() => handleEdit(hint)}
                              aria-label="Редактировать"
                              title="Редактировать"
                            >
                              <Pencil size={18} aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="px-icon-btn is-small is-danger"
                              onClick={() => handleDelete(hint.id)}
                              aria-label="Удалить"
                              title="Удалить"
                            >
                              <Trash2 size={18} aria-hidden="true" />
                            </button>
                          </div>
                        </div>

                        <pre
                          className="tool-hint-text"
                          tabIndex={0}
                          aria-label="Текст подсказки"
                        >
                          {hint.hint_text}
                        </pre>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })
      )}
    </div>
  );
};

export default AIPromptHintsPage;
